/**
 * Printing Page for HMH Lunch Ordering System
 *
 * Lets admins configure automatic daily printing of lunch orders:
 * enable/disable, print time (Central), and target CUPS printer.
 */

import { useEffect, useState } from "react";
import {
  useAutoprintSettings,
  useUpdateAutoprintSettings,
  usePrinters,
  useTestPrint,
} from "../../hooks/useAutoprint";
import {
  useMidnightSettings,
  useUpdateMidnightSettings,
  useMidnightTestPrint,
} from "../../hooks/useMidnight";

export function PrintingPage() {
  const { data: settings, isLoading, error } = useAutoprintSettings();
  const { data: printerData, isLoading: printersLoading, error: printersError, refetch: refetchPrinters } = usePrinters();
  const updateMutation = useUpdateAutoprintSettings();
  const testPrintMutation = useTestPrint();

  const [enabled, setEnabled] = useState(false);
  const [printTime, setPrintTime] = useState("10:05");
  const [printerName, setPrinterName] = useState("");
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setEnabled(settings.enabled);
      setPrintTime(settings.print_time);
      setPrinterName(settings.printer_name ?? "");
    }
  }, [settings]);

  const printers = printerData?.printers ?? [];
  // Keep a saved printer selectable even if CUPS is momentarily unreachable
  const printerOptions =
    printerName && !printers.some((p) => p.name === printerName)
      ? [{ name: printerName, state: "unknown", state_message: "", info: "", location: "" }, ...printers]
      : printers;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveMessage(null);
    setSaveError(null);

    try {
      await updateMutation.mutateAsync({
        enabled,
        print_time: printTime,
        printer_name: printerName,
      });
      setSaveMessage("Settings saved");
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save settings");
    }
  };

  const handleTestPrint = async () => {
    setSaveMessage(null);
    setSaveError(null);

    try {
      const result = await testPrintMutation.mutateAsync();
      setSaveMessage(`Test print sent to ${result.printer} (job ${result.job_id})`);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Test print failed");
    }
  };

  if (isLoading) {
    return <div className="text-gray-500">Loading printing settings...</div>;
  }

  if (error) {
    return <div className="text-red-600">Failed to load printing settings.</div>;
  }

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Printing</h1>

      <form onSubmit={handleSave} className="bg-white shadow rounded-lg p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Daily Autoprint</h2>
          <p className="text-sm text-gray-500 mt-1">
            Automatically print the day's lunch orders to a kitchen printer after the
            ordering cutoff. Times are US Central.
          </p>
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 text-hmh-blue focus:ring-hmh-blue"
          />
          <span className="font-medium text-gray-900">Enable daily autoprint</span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label htmlFor="print-time" className="block text-sm font-medium text-gray-700 mb-1">
              Print time (Central)
            </label>
            <input
              id="print-time"
              type="time"
              value={printTime}
              onChange={(e) => setPrintTime(e.target.value)}
              required
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-hmh-blue"
            />
            <p className="text-xs text-gray-500 mt-1">
              Lunch ordering closes at 10:00 AM — 10:05 AM prints the complete list.
            </p>
          </div>

          <div>
            <label htmlFor="printer" className="block text-sm font-medium text-gray-700 mb-1">
              Printer
            </label>
            <div className="flex gap-2">
              <select
                id="printer"
                value={printerName}
                onChange={(e) => setPrinterName(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-hmh-blue"
              >
                <option value="">— Select a printer —</option>
                {printerOptions.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name}
                    {p.state !== "unknown" ? ` (${p.state})` : ""}
                    {p.location ? ` — ${p.location}` : ""}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => refetchPrinters()}
                disabled={printersLoading}
                className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                title="Refresh printer list"
              >
                ↻
              </button>
            </div>
            {printersLoading && (
              <p className="text-xs text-gray-500 mt-1">Loading printers from print server...</p>
            )}
            {printersError && (
              <p className="text-xs text-red-600 mt-1">
                {printersError instanceof Error ? printersError.message : "Could not load printers"}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="px-4 py-2 bg-hmh-blue text-white rounded hover:opacity-90 disabled:opacity-50"
          >
            {updateMutation.isPending ? "Saving..." : "Save Settings"}
          </button>
          <button
            type="button"
            onClick={handleTestPrint}
            disabled={testPrintMutation.isPending || !printerName}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            {testPrintMutation.isPending ? "Printing..." : "Test Print Now"}
          </button>
          {saveMessage && <span className="text-sm text-green-700">{saveMessage}</span>}
          {saveError && <span className="text-sm text-red-600">{saveError}</span>}
        </div>
      </form>

      <div className="bg-white shadow rounded-lg p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Last Print</h2>
        {settings?.last_print_at ? (
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Status</dt>
              <dd
                className={`font-medium ${
                  settings.last_print_status === "success" ? "text-green-700" : "text-red-600"
                }`}
              >
                {settings.last_print_status === "success" ? "Success" : "Failed"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">When</dt>
              <dd className="text-gray-900">
                {new Date(`${settings.last_print_at}Z`).toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Detail</dt>
              <dd className="text-gray-900">{settings.last_print_detail || "—"}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-gray-500">Nothing has been printed yet.</p>
        )}
      </div>

      <MidnightSection printerOptions={printerOptions} />
    </div>
  );
}

/**
 * Midnight special configuration: overnight ordering window + nightly print.
 * Ships disabled; window/print times are placeholders until the kitchen
 * director confirms the schedule.
 */
function MidnightSection({
  printerOptions,
}: {
  printerOptions: { name: string; state: string; location: string }[];
}) {
  const { data: settings, isLoading } = useMidnightSettings();
  const updateMutation = useUpdateMidnightSettings();
  const testPrintMutation = useMidnightTestPrint();

  const [enabled, setEnabled] = useState(false);
  const [orderCutoff, setOrderCutoff] = useState("17:00");
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(7);
  const [allowSalad, setAllowSalad] = useState(true);
  const [offerSpecial, setOfferSpecial] = useState(false);
  const [printEnabled, setPrintEnabled] = useState(false);
  const [printTime, setPrintTime] = useState("22:05");
  const [printerName, setPrinterName] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setEnabled(settings.enabled);
      setOrderCutoff(settings.order_cutoff);
      setMaxAdvanceDays(settings.max_advance_days);
      setAllowSalad(settings.allow_salad);
      setOfferSpecial(settings.offer_special);
      setPrintEnabled(settings.print_enabled);
      setPrintTime(settings.print_time);
      setPrinterName(settings.printer_name ?? "");
    }
  }, [settings]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setErrorMsg(null);
    try {
      await updateMutation.mutateAsync({
        enabled,
        order_cutoff: orderCutoff,
        max_advance_days: maxAdvanceDays,
        allow_salad: allowSalad,
        offer_special: offerSpecial,
        print_enabled: printEnabled,
        print_time: printTime,
        printer_name: printerName,
      });
      setMessage("Midnight settings saved");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Failed to save settings");
    }
  };

  const handleTestPrint = async () => {
    setMessage(null);
    setErrorMsg(null);
    try {
      const result = await testPrintMutation.mutateAsync();
      setMessage(`Midnight test print sent to ${result.printer} (job ${result.job_id})`);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Test print failed");
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <form onSubmit={handleSave} className="bg-white shadow rounded-lg p-6 mt-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">🌙 Midnight Special (Overnight Shift)</h2>
        <p className="text-sm text-gray-500 mt-1">
          Nightly service for the 7PM–7AM shift, served at midnight. Staff pick a night up
          to a week ahead; same-day orders close at the cutoff below. The order list prints
          to the kitchen each night. Times are US Central.
        </p>
      </div>

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-5 w-5 rounded border-gray-300 text-hmh-blue focus:ring-hmh-blue"
        />
        <span className="font-medium text-gray-900">Enable midnight special ordering</span>
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Same-day cutoff</label>
          <input
            type="time"
            value={orderCutoff}
            onChange={(e) => setOrderCutoff(e.target.value)}
            required
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-hmh-blue"
          />
          <p className="text-xs text-gray-500 mt-1">
            Orders for the current night close at this time.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Days orderable in advance</label>
          <input
            type="number"
            min={1}
            max={30}
            value={maxAdvanceDays}
            onChange={(e) => setMaxAdvanceDays(Number(e.target.value))}
            required
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-hmh-blue"
          />
        </div>
        <div className="flex flex-col justify-end gap-2 pb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allowSalad}
              onChange={(e) => setAllowSalad(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-hmh-blue focus:ring-hmh-blue"
            />
            <span className="text-sm text-gray-700">Offer salad option</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={offerSpecial}
              onChange={(e) => setOfferSpecial(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-hmh-blue focus:ring-hmh-blue"
            />
            <span className="text-sm text-gray-700">
              Offer nightly special item <span className="text-gray-400">(off = salad only)</span>
            </span>
          </label>
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4 space-y-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={printEnabled}
            onChange={(e) => setPrintEnabled(e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 text-hmh-blue focus:ring-hmh-blue"
          />
          <span className="font-medium text-gray-900">Print midnight orders nightly</span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Print time (Central)</label>
            <input
              type="time"
              value={printTime}
              onChange={(e) => setPrintTime(e.target.value)}
              required
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-hmh-blue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Printer</label>
            <select
              value={printerName}
              onChange={(e) => setPrinterName(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-hmh-blue"
            >
              <option value="">Same as daily autoprint</option>
              {printerOptions.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.name}
                  {p.state !== "unknown" ? ` (${p.state})` : ""}
                  {p.location ? ` — ${p.location}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
        <button
          type="submit"
          disabled={updateMutation.isPending}
          className="px-4 py-2 bg-hmh-blue text-white rounded hover:opacity-90 disabled:opacity-50"
        >
          {updateMutation.isPending ? "Saving..." : "Save Midnight Settings"}
        </button>
        <button
          type="button"
          onClick={handleTestPrint}
          disabled={testPrintMutation.isPending}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          {testPrintMutation.isPending ? "Printing..." : "Test Print Midnight"}
        </button>
        {message && <span className="text-sm text-green-700">{message}</span>}
        {errorMsg && <span className="text-sm text-red-600">{errorMsg}</span>}
      </div>

      {settings?.last_print_at && (
        <p className="text-xs text-gray-500">
          Last midnight print:{" "}
          <span className={settings.last_print_status === "success" ? "text-green-700" : "text-red-600"}>
            {settings.last_print_status}
          </span>{" "}
          — {new Date(`${settings.last_print_at}Z`).toLocaleString()} ({settings.last_print_detail || "—"})
        </p>
      )}
    </form>
  );
}
